import type {
  NestedTree,
  NestedTreeNode,
  Tree,
  TreeNode,
} from "src/types/tree.types";
import type { CanvasPositionMap } from "src/features/editor";
import { nodeDefs } from "src/nodes";
import { NodeMap } from "src/types/node.types";

export function createTreeFromPositions(
  positions: CanvasPositionMap,
  nodes: NodeMap
): Tree {
  const nodeIdList = Object.keys(positions).sort((a, b) => {
    const posA = positions[a];
    const posB = positions[b];
    if (posA.y < posB.y) return -1;
    if (posA.y > posB.y) return 1;
    if (posA.x < posB.x) return -1;
    if (posA.x > posB.x) return 1;
    return 0;
  });

  const treeNodes = nodeIdList.map(
    (nodeId): TreeNode => ({
      node: nodes[nodeId],
      indent: positions[nodeId].x,
      isActive: true,
    })
  );

  const nodesWithHierarchy = treeNodes.reduce<TreeNode[]>((acc, node) => {
    const parent = getParentNode(treeNodes, node);

    if (parent) {
      const newParent = acc.find((n) => n.node.id === parent.node.id);
      const indent = newParent!.indent + 1;
      const newParentNodeDef = nodeDefs[parent.node.type];
      const isActive =
        newParent!.isActive && !!newParentNodeDef.acceptsChildren;
      acc.push({ ...node, indent, parentId: parent.node.id, isActive });
    } else {
      acc.push({ ...node, indent: 0 });
    }
    return acc;
  }, []);

  return { nodes: nodesWithHierarchy };
}

export function fixInactiveTreeNodes(tree: Tree, nodes: NodeMap): Tree {
  if (tree.nodes.every((node) => node.isActive)) return tree;

  const fixedNodes = tree.nodes.reduce<TreeNode[]>((acc, treeNode) => {
    if (treeNode.isActive) {
      acc.push(treeNode);
      return acc;
    }

    const fixedParent = (() => {
      const fixedTreeNode = acc.find((parent) => {
        return parent.node.id === treeNode.parentId;
      });

      const node = fixedTreeNode && nodes[fixedTreeNode.node.id];
      const nodeDef = node && nodeDefs[node.type];
      return fixedTreeNode && nodeDef
        ? { treeNode: fixedTreeNode, nodeDef }
        : undefined;
    })();

    const indent = (() => {
      if (!fixedParent) return 0;
      return fixedParent.nodeDef.acceptsChildren
        ? fixedParent.treeNode.indent + 1
        : fixedParent.treeNode.indent;
    })();

    const parentId = (() => {
      if (!fixedParent) return undefined;
      return fixedParent.nodeDef.acceptsChildren
        ? fixedParent.treeNode.node.id
        : fixedParent.treeNode.parentId;
    })();

    acc.push({ ...treeNode, isActive: true, indent, parentId });

    return acc;
  }, []);

  return { ...tree, nodes: fixedNodes };
}

export function createNestedTreeFromTree(tree: Tree): NestedTree {
  const shadowTree = shadowingTree(tree);
  const nestedTree: NestedTree = {
    nodes: nestTreeNodes(shadowTree.nodes),
  };

  return nestedTree;
}

function nestTreeNodes(nodes: TreeNode[]): NestedTreeNode[] {
  const nestedNodes: NestedTreeNode[] = [];

  const list = [...nodes];
  while (list.length > 0) {
    const node = list[0];
    const nextNodes = list.slice(1);
    const nextSiblingIndex = nextNodes.findIndex(
      (item) => item.indent <= node.indent
    );
    const children =
      nextSiblingIndex === -1
        ? nextNodes
        : nextNodes.slice(0, nextSiblingIndex);

    nestedNodes.push({
      node: node.node,
      isActive: node.isActive,
      children: nestTreeNodes(children),
    });

    list.splice(0, 1 + children.length);
  }

  return nestedNodes;
}

function shadowingTree(tree: Tree) {
  const shadowedNodeIdList = new Set();
  let shadowedTree = { ...tree, nodes: tree.nodes.slice() };

  const isComplete = () => {
    return shadowedTree.nodes.every((node) =>
      shadowedNodeIdList.has(node.node.id)
    );
  };

  while (!isComplete()) {
    const node = shadowedTree.nodes.find((node) => {
      return !shadowedNodeIdList.has(node.node.id);
    })!;

    const nodeDef = nodeDefs[node.node.type];
    shadowedNodeIdList.add(node.node.id);

    if (nodeDef.shadowingTree) {
      shadowedTree = nodeDef.shadowingTree(tree, node);
    }
  }

  return shadowedTree;
}

function getParentNode(treeNodes: TreeNode[], treeNode: TreeNode) {
  const index = treeNodes.indexOf(treeNode);

  for (let idx = index - 1; idx >= 0; idx--) {
    if (treeNodes[idx].indent < treeNode.indent) {
      return treeNodes[idx];
    }
  }

  return undefined;
}
